
import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, Pagination, Form, InputGroup, Button, Alert } from "react-bootstrap";
import { IconSearch, IconChevronUp, IconChevronDown } from "@tabler/icons-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { Helmet } from "react-helmet-async";

const APILogs = () => {
  const [logs, setLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [error, setError] = useState(null);

  // --- helpers ---
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const getPaginationPages = (current, total) => {
    const delta = 2;
    const pages = new Set();
    pages.add(1);
    pages.add(total);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  const allChecked = logs.length > 0 && selectedLogs.length === logs.length;

  // --- data ---
  const fetchLogs = useCallback(async () => {
    try {
      setError(null);
      const params = new URLSearchParams({
        page,
        limit,
        search: searchQuery,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      const response = await fetchWithAuth(`/api/admin/api-logs?${params}`);
      if (!response.ok) throw new Error("Failed to load logs");
      const data = await response.json();
      setLogs(data.results || []);
      setTotal(data.total || 0);
      // сброс выделения при смене страницы/лимита/поиска
      setSelectedLogs([]);
    } catch (e) {
      console.error("Ошибка при загрузке логов:", e);
      setError("Ошибка при загрузке логов");
      setLogs([]);
      setTotal(0);
    }
  }, [page, limit, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // --- actions ---
  const handleDeleteSelected = async () => {
    if (selectedLogs.length === 0) return;
    const confirmed = window.confirm(`Удалить выбранные записи (${selectedLogs.length})?`);
    if (!confirmed) return;
    try {
      const res = await fetchWithAuth("/api/admin/api-logs", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(selectedLogs),
      });
      if (!res.ok) throw new Error("Ошибка удаления");
      await fetchLogs();
    } catch (e) {
      alert(e.message || "Ошибка удаления");
    }
  };

  const handleDeleteOlderThan3Months = async () => {
    const confirmed = window.confirm("Удалить все записи старше 3 месяцев?");
    if (!confirmed) return;
    try {
      const res = await fetchWithAuth("/api/admin/api-logs/older-than-3-months", {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Ошибка удаления");
      await fetchLogs();
    } catch (e) {
      alert(e.message || "Ошибка удаления");
    }
  };

  // --- UI helpers ---
  const renderSortHeader = (label, key) => (
    <th
      onClick={() => {
        if (sortBy === key) {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
          setSortBy(key);
          setSortOrder("asc");
        }
      }}
      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
    >
      {label}{" "}
      {sortBy === key &&
        (sortOrder === "asc" ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />)}
    </th>
  );

  return (<><Helmet> <title>API Logs - IndexBooster</title></Helmet>

    <Card className="mt-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h3 className="m-0">API Logs</h3>
        <InputGroup style={{ maxWidth: 300 }}>
          <InputGroup.Text>
            <IconSearch size={18} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search by user email"
            value={searchQuery}
            onChange={(e) => {
              setPage(1);
              setSearchQuery(e.target.value);
            }}
          />
        </InputGroup>
      </Card.Header>

      {error && <Alert variant="danger" className="m-3 mb-0">{error}</Alert>}

      <div className="table-responsive">
        <Table className="table card-table table-vcenter text-nowrap datatable" hover responsive>
          <thead>
            <tr>
              <th className="w-1">
                <Form.Check
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedLogs(logs.map((l) => l.id));
                    else setSelectedLogs([]);
                  }}
                />
              </th>
              {renderSortHeader("User", "user_email")}
              {renderSortHeader("Request", "request_type")}
              {renderSortHeader("Status", "response_status")}
              {renderSortHeader("IP Address", "ip_address")}
              {renderSortHeader("Date", "created_at")}
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-4">
                  No Data
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id}>
                  <td className="w-1">
                    <Form.Check
                      type="checkbox"
                      checked={selectedLogs.includes(log.id)}
                      onChange={(e) => {
                        setSelectedLogs((prev) =>
                          e.target.checked ? [...prev, log.id] : prev.filter((id) => id !== log.id)
                        );
                      }}
                    />
                  </td>
                  <td>
                    <a href={`/user/${log.user_id}/projects`} rel="noreferrer">
                      {log.user_email}
                    </a>
                  </td>
                  <td>{log.request_type}</td>
                  <td>{log.response_status}</td>
                  <td>{log.ip_address || "-"}</td>
                  <td>
                    {new Date(log.created_at).toISOString().slice(0, 10).split("-").reverse().join(".")}
                    <br />
                    {new Date(log.created_at).toISOString().slice(11, 19)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Card.Footer className="px-4">
        <div className="d-flex flex-column gap-2 w-100">

          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 w-100">
            <div className="d-flex align-items-center flex-wrap gap-3">
              <Pagination className="m-0 flex-wrap">
                <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)}>«</Pagination.Prev>
                {getPaginationPages(page, totalPages).map((p, i) =>
                  p === "..." ? (
                    <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={`page-${p}`}
                      active={p === page}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)}>»</Pagination.Next>
              </Pagination>

              <div className="text-muted">
                Showing <b>{(page - 1) * limit + 1}</b>–<b>{Math.min(page * limit, total)}</b> of <b>{total}</b> entries
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 ms-md-auto">
              <span className="text-muted">Show</span>
              <Form.Select
                value={limit}
                onChange={(e) => {
                  setPage(1);
                  setLimit(Number(e.target.value));
                }}
                size="sm"
              >
                {[10, 25, 50, 100, 200, 500].map((val) => (
                  <option key={val} value={val}>{val}</option>
                ))}
              </Form.Select>
              <span className="text-muted">entries</span>
            </div>
          </div>

          <div className="d-flex justify-content-start gap-2">
            <Button
              variant="danger"
              disabled={selectedLogs.length === 0}
              onClick={handleDeleteSelected}
            >
              Delete Selected
            </Button>
            <Button
              variant="outline-danger"
              onClick={handleDeleteOlderThan3Months}
            >
              Delete older than 3 months
            </Button>
          </div>
        </div>
      </Card.Footer>
    </Card>
 </> );
};

export default APILogs;
